/**
 * SharePoint site and list configuration for Harmony AP.
 *
 * All lists live on the dedicated Harmony AP site (/sites/HarmonyAP),
 * which also hosts the AP-Comply lists (AP_Master etc.).
 */

/** SharePoint site URL in Graph API format (hostname:/path) */
export const SHAREPOINT_SITE_URL =
  'harmonyhc1.sharepoint.com:/sites/HarmonyAP';

/** Display names for all Harmony AP SharePoint lists */
export const LIST_NAMES = {
  shifts: 'HarmonyAP_Shifts',
  attendance: 'HarmonyAP_Attendance',
  progressNotes: 'HarmonyAP_ProgressNotes',
  clientProfiles: 'HarmonyAP_ClientProfiles',
  apMaster: 'AP_Master',
  documentDelivery: 'HarmonyAP_DocumentDelivery',
  complianceDocuments: 'HarmonyAP_ComplianceDocuments',
  documentReleases: 'HarmonyAP_DocumentReleases',
} as const;

/** List name key type for type-safe list references */
export type ListNameKey = keyof typeof LIST_NAMES;

// ---------------------------------------------------------------------------
// Hardcoded Graph object IDs (gathered 26 Jun 2026 from /sites/HarmonyAP)
//
// AP guests are Entra B2B users with only **Limited Access** at the web, so
// they cannot resolve site/list/drive objects by name (those calls require
// web-level read and return 403). We therefore skip every by-name/enumeration
// lookup and address the granted objects directly by their stable IDs, which
// the guest's per-list grants allow. This keeps the AP-Comply register — which
// inherits the web's permissions — invisible to guests.
// ---------------------------------------------------------------------------

/** Graph site ID for /sites/HarmonyAP (host,siteCollectionId,siteId triple). */
export const SHAREPOINT_SITE_ID =
  'harmonyhc1.sharepoint.com,c9f16dd5-5b98-47b6-aa90-40e44dba01cc,de5f3292-428e-4db9-8d0b-cfce9a613d5f';

/** Hardcoded list GUIDs keyed by the same keys as LIST_NAMES. */
export const LIST_IDS: Record<ListNameKey, string> = {
  shifts: '4b411277-2708-4dd5-be7c-aaaf7612a4c3',
  attendance: 'f54d7b66-3d0f-4df6-a4e6-83e03392bf79',
  progressNotes: '8b0c2e24-adf6-4586-8636-c54aec289a50',
  clientProfiles: '05a9f05d-6b76-487b-834b-a4456d66dda4',
  apMaster: '2ad9bcbf-4ef5-455d-a3a7-babd44c03fc8',
  documentDelivery: 'e82f7dbb-f0a9-4a15-a700-addc91182f6a',
  complianceDocuments: 'a4b68003-0507-4d1a-9ee0-82f1db885aae',
  // Phase 34 release record (provisioned 2026-09-02 by
  // scripts/provision-document-releases.sh). AP document visibility derives
  // from a row here, never from file presence in AP_Documents/Clients/.
  documentReleases: '46aea715-3993-4aa2-97ec-b20679c9c8d2',
};

/** Reverse lookup: list display name → hardcoded list GUID. */
export const LIST_ID_BY_NAME: Record<string, string> = Object.fromEntries(
  (Object.keys(LIST_NAMES) as ListNameKey[]).map((key) => [
    LIST_NAMES[key],
    LIST_IDS[key],
  ]),
);

/** Display name of the AP documents library (a drive, not a list). */
export const AP_DOCUMENTS_LIBRARY_NAME = 'AP_Documents';

/**
 * Hardcoded drive ID for the AP_Documents library. Client-document viewing and
 * compliance uploads need this to skip the `/sites/{siteId}/drives` enumeration
 * that the guest cannot perform.
 */
export const AP_DOCUMENTS_DRIVE_ID =
  'b!1W3xyZhbtkeqkEDkTboBzJIyX96OQrlNjQvPzpphPV9fRGGtX40vSYTEfzzvlR5U';

/**
 * Build a Graph API URL for list items.
 *
 * @example
 * buildListItemsUrl('site-id-123', 'list-id-456')
 * // → '/sites/site-id-123/lists/list-id-456/items'
 */
export function buildListItemsUrl(siteId: string, listId: string): string {
  return `/sites/${siteId}/lists/${listId}/items`;
}

/**
 * Build a Graph API URL for a single list item.
 *
 * @example
 * buildListItemUrl('site-id-123', 'list-id-456', '7')
 * // → '/sites/site-id-123/lists/list-id-456/items/7'
 */
export function buildListItemUrl(
  siteId: string,
  listId: string,
  itemId: string,
): string {
  return `/sites/${siteId}/lists/${listId}/items/${itemId}`;
}
